import { ButtonHTMLAttributes, forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

// appearance-none/border-0/bg-transparent/p-0 burada taban (base) sınıf olarak
// veriliyor; native <button> chrome'unu sıfırlamak için. Bunları ayrı bir global
// CSS reset dosyasında yapmak yerine burada, cn()/tailwind-merge üzerinden
// vermemizin sebebi: CRA'nın build zinciri @layer'ı korumuyor (base/utilities
// aynı specificity'ye düşüyor), bu yüzden global bir ".idare-root button {...}"
// kuralı variant class'larını (örn. bg-[hsl(var(--primary))]) ezip butonları
// görünmez yapıyordu. tailwind-merge, aynı class string içinde SONRA gelen
// çakışan class'ı (variant) koruyup öncekini (base) attığı için bu sorunu
// deterministik şekilde çözüyor.
const buttonVariants = cva(
  // text-[hsl(var(--foreground))] burada da BASE seviyesinde: index.scss'teki
  // global "button { color: white }" kuralina karsi varsayilan rengi geri
  // kazandiriyor. ghost/outline gibi kendi text-* utility'sini set etmeyen
  // variant'lar bunu kullanir; default/destructive kendi text-[...] class'lariyla
  // (tailwind-merge sayesinde) bunun ustune yazar.
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border-0 bg-transparent p-0 m-0 appearance-none cursor-pointer text-sm font-medium text-[hsl(var(--foreground))] transition-colors disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        default: 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90',
        destructive: 'bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))] hover:opacity-90',
        outline: 'border border-[hsl(var(--border))] bg-transparent hover:bg-[hsl(var(--secondary))]',
        ghost: 'hover:bg-[hsl(var(--secondary))]',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
  )
);
Button.displayName = 'Button';
